import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Square, Trash2, Play, Pause, Send, AlertTriangle } from "lucide-react";

// WhatsApp usa notas de voz en ogg/opus; preferimos ese contenedor y caemos a
// webm/opus (Chrome) o mp4. Evolution convierte a ptt del lado servidor, así que
// cualquiera de estos sirve como entrada.
function pickAudioMime() {
  if (typeof MediaRecorder === "undefined") return "";
  const types = [
    "audio/ogg;codecs=opus",
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
  ];
  for (const ty of types) {
    try {
      if (MediaRecorder.isTypeSupported(ty)) return ty;
    } catch {
      /* isTypeSupported puede lanzar en navegadores viejos */
    }
  }
  return "";
}

function extForMime(mime) {
  if (!mime) return "ogg";
  if (mime.includes("ogg")) return "ogg";
  if (mime.includes("webm")) return "webm";
  if (mime.includes("mp4")) return "m4a";
  return "ogg";
}

function fmtTime(ms) {
  const total = Math.floor((ms || 0) / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

// Promedia los picos en `n` cubos para dibujar el waveform estático del preview.
function downsample(peaks, n) {
  if (peaks.length <= n) return peaks.slice();
  const out = new Array(n).fill(0);
  const bucket = peaks.length / n;
  for (let i = 0; i < n; i++) {
    const start = Math.floor(i * bucket);
    const end = Math.floor((i + 1) * bucket);
    let sum = 0;
    let cnt = 0;
    for (let j = start; j < end; j++) {
      sum += peaks[j];
      cnt++;
    }
    out[i] = cnt ? sum / cnt : 0;
  }
  return out;
}

const MAX_RECORD_MS = 5 * 60 * 1000; // corte de seguridad: 5 min

/**
 * Grabador de nota de voz para la mesa de ayuda.
 * - Se monta ya grabando (el padre lo monta al tocar el micrófono).
 * - Tap en Detener para terminar; tap en Cancelar para descartar.
 * - Waveform en <canvas>: en vivo mientras graba, estático + progreso en preview.
 * - Al enviar entrega un File (audio/ogg|webm opus) al padre vía onSend.
 *
 * @param {object} props
 * @param {(file: File) => void} props.onSend
 * @param {() => void} props.onCancel
 * @param {boolean} [props.sending]  - el padre está enviando (muestra spinner)
 */
export default function VoiceRecorder({ onSend, onCancel, sending = false }) {
  const { t } = useTranslation();
  const [phase, setPhase] = useState("starting"); // starting | recording | recorded | error
  const [elapsedMs, setElapsedMs] = useState(0);
  const [durationMs, setDurationMs] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const peaksRef = useRef([]);
  const startRef = useRef(0);
  const lastPeakRef = useRef(0);
  const lastSecRef = useRef(-1);
  const canvasRef = useRef(null);
  const audioElRef = useRef(null);
  const urlRef = useRef(null);
  const sentRef = useRef(false);
  const mountedRef = useRef(true);
  const stopRef = useRef(null); // setter de "detener", asignado en el efecto de arranque

  // ── Dibujo del waveform (imperativo, sin re-render de React) ──────────────
  const draw = useCallback(({ live = false, progress = null } = {}) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const cssW = canvas.clientWidth || 180;
    const cssH = canvas.clientHeight || 32;
    if (canvas.width !== Math.floor(cssW * dpr)) canvas.width = Math.floor(cssW * dpr);
    if (canvas.height !== Math.floor(cssH * dpr)) canvas.height = Math.floor(cssH * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cssW, cssH);

    const barW = 2.5;
    const step = barW + 2;
    const maxBars = Math.max(1, Math.floor(cssW / step));

    let data;
    let offset = 0;
    if (live) {
      data = peaksRef.current.slice(-maxBars);
      offset = Math.max(0, cssW - data.length * step); // scroll a la derecha
    } else {
      data = downsample(peaksRef.current, maxBars);
    }

    // Color activo del tema (via className text-brand-500); inactivo gris suave.
    const activeColor = getComputedStyle(canvas).color || "rgb(59,130,246)";
    const idleColor = "rgba(148,163,184,0.5)";
    const mid = cssH / 2;
    const playedUntil = progress == null ? -1 : Math.floor(progress * data.length);

    for (let i = 0; i < data.length; i++) {
      const h = Math.max(2, data[i] * (cssH - 4));
      const x = offset + i * step;
      const y = mid - h / 2;
      if (progress != null) {
        ctx.fillStyle = i <= playedUntil ? activeColor : idleColor;
      } else {
        ctx.fillStyle = live ? activeColor : idleColor;
      }
      ctx.fillRect(x, y, barW, h);
    }
  }, []);

  // ── Limpieza de recursos ──────────────────────────────────────────────────
  const cleanup = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    try {
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.stop();
      }
    } catch {
      /* noop */
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((tr) => tr.stop());
      streamRef.current = null;
    }
    if (audioCtxRef.current) {
      try {
        audioCtxRef.current.close();
      } catch {
        /* noop */
      }
      audioCtxRef.current = null;
    }
    if (audioElRef.current) {
      try {
        audioElRef.current.pause();
      } catch {
        /* noop */
      }
    }
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
  }, []);

  // ── Arranca la grabación al montar ─────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const tick = () => {
      const analyser = analyserRef.current;
      if (!analyser) return;
      const buf = new Uint8Array(analyser.fftSize);
      analyser.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) {
        const v = (buf[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / buf.length);
      const now = performance.now();
      if (now - lastPeakRef.current >= 90) {
        lastPeakRef.current = now;
        peaksRef.current.push(Math.min(1, rms * 2.4));
      }
      const el = now - startRef.current;
      const sec = Math.floor(el / 1000);
      if (sec !== lastSecRef.current) {
        lastSecRef.current = sec;
        setElapsedMs(el); // re-render del timer solo 1×/seg
      }
      draw({ live: true });
      if (el >= MAX_RECORD_MS) {
        // eslint-disable-next-line no-use-before-define
        stop();
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    const stop = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      setDurationMs(performance.now() - startRef.current);
      try {
        if (recorderRef.current && recorderRef.current.state !== "inactive") {
          recorderRef.current.stop(); // dispara onstop → arma el blob
        }
      } catch {
        /* noop */
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((tr) => tr.stop());
        streamRef.current = null;
      }
      if (audioCtxRef.current) {
        try {
          audioCtxRef.current.close();
        } catch {
          /* noop */
        }
        audioCtxRef.current = null;
      }
    };
    stopRef.current = stop;

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (cancelled) {
          stream.getTracks().forEach((tr) => tr.stop());
          return;
        }
        streamRef.current = stream;

        const mime = pickAudioMime();
        const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
        recorderRef.current = rec;
        chunksRef.current = [];
        rec.ondataavailable = (e) => {
          if (e.data && e.data.size) chunksRef.current.push(e.data);
        };
        rec.onstop = () => {
          const type = rec.mimeType || mime || "audio/webm";
          const blob = new Blob(chunksRef.current, { type });
          if (urlRef.current) URL.revokeObjectURL(urlRef.current);
          urlRef.current = URL.createObjectURL(blob);
          if (audioElRef.current) audioElRef.current.src = urlRef.current;
          setPhase("recorded");
          requestAnimationFrame(() => draw({ progress: 0 }));
        };
        rec.start();

        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        const audioCtx = new AudioCtx();
        audioCtxRef.current = audioCtx;
        if (audioCtx.state === "suspended") {
          try {
            await audioCtx.resume();
          } catch {
            /* noop */
          }
        }
        const source = audioCtx.createMediaStreamSource(stream);
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 512;
        source.connect(analyser);
        analyserRef.current = analyser;

        startRef.current = performance.now();
        lastPeakRef.current = 0;
        lastSecRef.current = -1;
        peaksRef.current = [];
        setPhase("recording");
        rafRef.current = requestAnimationFrame(tick);
      } catch (err) {
        if (cancelled) return;
        const name = err?.name || "";
        setErrorMsg(
          name === "NotAllowedError" || name === "SecurityError"
            ? t("Permiso de micrófono denegado.")
            : name === "NotFoundError"
              ? t("No se encontró un micrófono.")
              : t("No se pudo iniciar la grabación."),
        );
        setPhase("error");
      }
    })();

    return () => {
      cancelled = true;
      cleanup();
    };
    // Solo al montar: arranca la grabación una vez.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStop = useCallback(() => {
    if (stopRef.current) stopRef.current();
  }, []);

  const handleCancel = useCallback(() => {
    cleanup();
    onCancel?.();
  }, [cleanup, onCancel]);

  const handleConfirm = useCallback(() => {
    if (sentRef.current) return;
    const chunks = chunksRef.current;
    if (!chunks.length) return;
    sentRef.current = true;
    const type = recorderRef.current?.mimeType || pickAudioMime() || "audio/ogg";
    const blob = new Blob(chunks, { type });
    const file = new File([blob], `voice-note-${Date.now()}.${extForMime(type)}`, {
      type,
    });
    // El guard evita el doble envío mientras el padre procesa. Si el envío sale bien el
    // padre desmonta el grabador; si falla, el grabador sigue montado con la misma nota y
    // el guard se rearma para que "Enviar" vuelva a funcionar (antes quedaba muerto y había
    // que descartar y grabar de nuevo).
    let pending;
    try {
      pending = onSend?.(file);
    } catch (err) {
      pending = Promise.reject(err);
    }
    Promise.resolve(pending)
      .catch(() => {})
      .finally(() => {
        if (mountedRef.current) sentRef.current = false;
      });
  }, [onSend]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // ── Reproducción del preview ────────────────────────────────────────────────
  const togglePlay = useCallback(() => {
    const el = audioElRef.current;
    if (!el) return;
    if (el.paused) {
      el.play().catch(() => {});
      setIsPlaying(true);
    } else {
      el.pause();
      setIsPlaying(false);
    }
  }, []);

  const onTimeUpdate = useCallback(() => {
    const el = audioElRef.current;
    if (!el || !durationMs) return;
    const progress = Math.min(1, (el.currentTime * 1000) / durationMs);
    draw({ progress });
  }, [draw, durationMs]);

  const onEnded = useCallback(() => {
    setIsPlaying(false);
    draw({ progress: 0 });
  }, [draw]);

  const iconBtn =
    "inline-flex w-11 h-11 md:w-9 md:h-9 items-center justify-center rounded-md text-fg-muted hover:bg-bg-muted hover:text-fg disabled:opacity-50 transition-colors shrink-0";
  const primaryBtn =
    "hd-brand-solid inline-flex w-11 h-11 md:w-9 md:h-9 items-center justify-center rounded-md bg-brand-500 text-bg hover:bg-brand-600 active:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0";

  if (phase === "error") {
    return (
      <div className="flex items-center gap-2 w-full rounded-md border border-border bg-bg px-3 py-2">
        <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" aria-hidden="true" />
        <span className="flex-1 text-sm text-fg-muted">{errorMsg}</span>
        <button type="button" onClick={handleCancel} className={iconBtn} aria-label={t("Cerrar")}>
          <Trash2 className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>
    );
  }

  const recorded = phase === "recorded";

  return (
    <div className="flex items-center gap-2 w-full rounded-md border border-border bg-bg px-2 py-1.5">
      {/* Cancelar (descarta) */}
      <button
        type="button"
        onClick={handleCancel}
        disabled={sending}
        className={iconBtn}
        aria-label={t("Descartar nota de voz")}
        title={t("Descartar")}
      >
        <Trash2 className="w-4 h-4" aria-hidden="true" />
      </button>

      {/* Play/Pause (solo en preview) */}
      {recorded && (
        <button
          type="button"
          onClick={togglePlay}
          disabled={sending}
          className={iconBtn}
          aria-label={isPlaying ? t("Pausar") : t("Reproducir")}
          title={isPlaying ? t("Pausar") : t("Reproducir")}
        >
          {isPlaying ? (
            <Pause className="w-4 h-4" aria-hidden="true" />
          ) : (
            <Play className="w-4 h-4" aria-hidden="true" />
          )}
        </button>
      )}

      {/* Punto rojo mientras graba */}
      {!recorded && (
        <span
          className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse shrink-0"
          aria-hidden="true"
        />
      )}

      {/* Waveform */}
      <canvas ref={canvasRef} className="flex-1 h-8 min-w-0 text-brand-500" />

      {/* Tiempo (transcurrido mientras graba, duración en preview) */}
      <span className="text-xs tabular-nums text-fg-muted shrink-0 w-10 text-right">
        {fmtTime(recorded ? durationMs : elapsedMs)}
      </span>

      {/* Elemento de audio oculto para el preview */}
      <audio
        ref={audioElRef}
        className="hidden"
        onTimeUpdate={onTimeUpdate}
        onEnded={onEnded}
        preload="metadata"
      />

      {/* Detener (grabando) o Enviar (preview) */}
      {recorded ? (
        <button
          type="button"
          onClick={handleConfirm}
          disabled={sending}
          className={primaryBtn}
          aria-label={t("Enviar nota de voz")}
          title={t("Enviar")}
        >
          {sending ? (
            <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
          ) : (
            <Send className="w-4 h-4" aria-hidden="true" />
          )}
        </button>
      ) : (
        <button
          type="button"
          onClick={handleStop}
          disabled={phase !== "recording"}
          className={primaryBtn}
          aria-label={t("Detener grabación")}
          title={t("Detener")}
        >
          <Square className="w-4 h-4 fill-current" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
